import React, { ChangeEvent, Fragment, useEffect, useMemo, useState } from 'react';
// i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useToggle from 'hooks/useToggle';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { deleteMedia, getMedia, saveGeneratedMedia } from 'store/media/mediaAsync';
import { mediaActions } from 'store/media/mediaSlice';
import { selectQuiz } from 'store/quiz/quizzesSelectors';
import { selectMedia, selectSelectedMedia } from 'store/media/mediaSelectors';
import { selectAIModuleFeatureEnable } from 'store/currentUser/currentUserSelectors';
import IMedia from 'models/Media';
import MediaFormats from 'types/MediaFormats';
// Icons
import { FaTrash, FaEye, FaMagic } from 'react-icons/fa';
// Layouts
import { Dialog, DialogTitle, DialogContent, DialogActions } from 'layouts/Dialog';
// Components
import { Button, IconButton } from 'components/Buttons';
import { Select } from 'components/Controls';
import { Loader } from 'components/Utilities';
import AudioPlayer from 'components/AudioPlayer';
import MediaPreviewDialog from './MediaPreview.dialog';
import ConfirmationDialog from './Confirmation.dialog';
import UploadImageDialog from './UploadImage.dialog';
import UploadVideoDialog from './UploadVideo.dialog';
import UploadAudioDialog from './UploadAudio.dialog';
import GenerateImageDialog from './GenerateImage.dialog';
// Utilities
import { mediasInfo } from 'utilities/utilities';

type Props = {
  open: boolean;
  onClose: () => void;
  type: 'image' | 'video' | 'audio';
  onChange: (mediaUrl: string) => void;
  initialFormat?: MediaFormats;
}

const MediaDialog:React.FC<Props> = ({ open, onClose, type, onChange, initialFormat = MediaFormats.Logo }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const quiz = useAppSelector(selectQuiz);
  const media = useAppSelector(selectMedia);
  const [format, setFormat] = useState<MediaFormats | string>(initialFormat);

  const selectedMedia = useAppSelector(selectSelectedMedia);
  const aiModuleEnabled = useAppSelector(selectAIModuleFeatureEnable);

  const handleFormat = (e:ChangeEvent<HTMLSelectElement>) => {
    setFormat(e.target.value);
  }

  const filteredMedia = useMemo(() => {
    return media.filter(media => {
      const formatFilter = format === 'all' || type !== 'image' ? true : media.format === format;
      const typeFilter = media.type === type;
      return formatFilter && typeFilter;
    });
  }, [media, format, type]);

  const { open:openPreview, toggle:togglePreview } = useToggle();
  const { open:openDeleteDialog, toggle:toggleDeleteDialog } = useToggle();

  const onClick = (media: IMedia) => {
    dispatch(mediaActions.setSelectedMedia(media));
  }

  const handleDelete = () => {
    if (!selectedMedia) return;
    dispatch(deleteMedia(selectedMedia._id));
  }

  const handleClickApply = () => {
    if (!selectedMedia) return;
    onChange('');
    setTimeout(() => onChange(selectedMedia.href), 0);
    onClose();
  }

  useEffect(() => {
    setIsLoading(true);
    let params:any = { scope: ['account'] };
    if (quiz) params = { scope: ['account', 'quiz'], quizId: quiz._id };
    dispatch(getMedia(params)).unwrap()
      .finally(() => setIsLoading(false));

    return () => {
      dispatch(mediaActions.setInitialField('media'));
      dispatch(mediaActions.setInitialField('selectedMedia'));
    }
    // eslint-disable-next-line
  }, []);

  const { open:openImageUploadDialog, toggle:toggleImageUploadDialog } = useToggle();
  const { open:openVideoUploadDialog, toggle:toggleVideoUploadDialog } = useToggle();
  const { open:openAudioUploadDialog, toggle:toggleAudioUploadDialog } = useToggle();

  const openUploadDialog = () => {
    if (type === 'image') toggleImageUploadDialog();
    if (type === 'video') toggleVideoUploadDialog();
    if (type === 'audio') toggleAudioUploadDialog();
  }

  const { open:openGenerateDialog, toggle:toggleGenerateDialog } = useToggle();

  const handleApplyGeneratedImage = (url: string, generatedFormat: MediaFormats) => {
    setIsLoading(true);
    dispatch(saveGeneratedMedia({ url, format: generatedFormat, quizId: quiz?._id })).unwrap()
      .then((media: IMedia) => {
        // Switch the filter to the generated image's format so it stays visible and selected
        if (format !== 'all') setFormat(generatedFormat);
        dispatch(mediaActions.setSelectedMedia(media));
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }

  const isDisabled = !selectedMedia;

  const formatOptions = [
    { value: 'all', label: t('labels.all') },
    ...(Object.keys(MediaFormats) as Array<keyof typeof MediaFormats>)
    .map((key) => ({ value: MediaFormats[key], label: mediasInfo[MediaFormats[key]].label }))
  ];

  return (
    <Fragment>
      {openImageUploadDialog ? (
        <UploadImageDialog
          open={openImageUploadDialog}
          onClose={toggleImageUploadDialog}
          initialFormat={initialFormat}
        />
      ) : null}

      {openVideoUploadDialog ? (
        <UploadVideoDialog
          open={openVideoUploadDialog}
          onClose={toggleVideoUploadDialog}
        />
      ) : null}

      {openAudioUploadDialog ? (
        <UploadAudioDialog
          open={openAudioUploadDialog}
          onClose={toggleAudioUploadDialog}
        />
      ) : null}

      {aiModuleEnabled && openGenerateDialog ? (
        <GenerateImageDialog
          open={openGenerateDialog}
          onClose={toggleGenerateDialog}
          format={format !== 'all' ? format as MediaFormats : initialFormat}
          allowFormatChange
          onApply={handleApplyGeneratedImage}
        />
      ) : null}

      {openPreview ? (
        <MediaPreviewDialog open={openPreview} onClose={togglePreview} media={selectedMedia} />
      ) : null}

      {openDeleteDialog ? (
        <ConfirmationDialog
          open={openDeleteDialog}
          onClose={toggleDeleteDialog}
          title={t('dialogs.media.deleteTitle')}
          content={t('dialogs.media.deleteConfirmation')}
          loading={false}
          onConfirm={handleDelete}
        />
      ) : null}

      <Dialog size="xl" open={open} onClose={onClose}>
        <DialogTitle title={t('dialogs.media.title')} onClose={onClose} />
        <DialogContent>
          {isLoading && <Loader />}
          {type === 'image' && (
            <Fragment>
              <Select
                id="media-format" name="media-format"
                options={formatOptions}
                value={format}
                onChange={handleFormat}
                label={t('labels.format')}
              />
              {format !== 'all' && (
                <span className="block text-gray-400 text-sm font-light leading-5 mb-4">{mediasInfo[format as MediaFormats].hint}</span>
              )}
            </Fragment>
          )}
          <div className="flex flex-wrap gap-4 mb-2">
            {filteredMedia.map(m => (
              <div
                key={m._id}
                onClick={() => onClick(m)}
                className="w-56 h-32 relative border"
                style={{
                  boxShadow: selectedMedia?._id === m._id ? '0 10px 15px -3px rgba(255, 255, 255, 0.2), 0 4px 6px -4px rgba(255, 255, 255, 0.4)' : 'none',
                  borderColor: selectedMedia?._id === m._id ? '#ff9000' : 'inherit',
                }}
              >
                {m.type === 'image' ? (
                  <div className="w-full h-full" style={{
                    background: `url('${m.href}') no-repeat center / ${format === 'all' ? 'cover' : 'contain' }`,
                    cursor: 'pointer'
                  }} />
                ) : null}
                {m.type === 'video' ? (
                  <video className="w-full h-full object-center object-cover" controls>
                    <source src={m.href} type="video/mp4" />
                  </video>
                ) : null}
                {m.type === 'audio' ? (
                  <div className="w-full h-full flex flex-col justify-end">
                    <p className="text-white text-sm break-all p-1">{m.title || ''}</p>
                    <AudioPlayer src={m.href} />
                  </div>
                ) : null}
                <div className="absolute top-0 right-0 bg-gray-800 rounded">
                  <IconButton size="sm" onClick={togglePreview}>
                    <FaEye />
                  </IconButton>
                  <IconButton size="sm" onClick={toggleDeleteDialog} className="hover:text-red-600">
                    <FaTrash />
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={openUploadDialog}
            variant="outlined"
          >{t('dialogs.media.uploadButton')}</Button>
          {aiModuleEnabled && type === 'image' ? (
            <Button
              className="ml-2"
              onClick={toggleGenerateDialog}
              variant="outlined"
            ><FaMagic className="inline-block mr-2 -mt-0.5" />{t('buttons.generateWithAi')}</Button>
          ) : null}
          <div className="flex-grow" />
          <Button
            onClick={onClose}
          >{t('buttons.cancel')}</Button>
          <Button
            className="ml-2"
            variant="contained"
            disabled={isDisabled}
            onClick={handleClickApply}
          >{t('buttons.apply')}</Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  )
}

export default MediaDialog;

